/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Display formatting for HorizontalSelectionDialog option tiles.
 *
 * Figma sets a tile's label in two sizes — the numeric value in TV/Bold/H4
 * (42/54) above the unit in 28/40 — so "10 minutes early" renders as a large
 * "10" over a small "minutes early". Labels with no leading number ("On time")
 * are a single line in TV/Bold/H6 (34/39) instead.
 *
 * The engine hands the overlay one flat string per option, so the split happens
 * here rather than in the view model: it is a typography concern, and the
 * original label is still what round-trips back to the engine on save.
 */

/** A tile label divided into the two type sizes the design uses. */
export interface IOptionLabelParts {
  /**
   * Leading numeric part, set large. `null` when the label does not start with
   * a number, in which case the whole label is a single line.
   */
  value: string | null;
  /** Everything after the number, set small beneath it. Empty if none. */
  unit: string;
}

/**
 * Leading integer or decimal plus the whitespace that separates it from the
 * unit. Decimals matter: Stop Recording offers "1.5 hours longer".
 */
const LEADING_NUMBER: RegExp = /^(\d+(?:\.\d+)?)\s*/;

/**
 * Leading mixed number ("1 1/2 hours longer") or bare fraction ("1/2 hour
 * longer"). The engine passes the service's own wording through, and Stop
 * Recording writes its half-hours as fractions, while Figma sets the same tile
 * as a decimal — "1.5". Checked before {@link LEADING_NUMBER}, which would
 * otherwise take the "1" of "1/2" and leave "/2" behind as the unit.
 */
const LEADING_MIXED_FRACTION: RegExp = /^(\d+)\s+(\d+)\/(\d+)\s*/;
const LEADING_FRACTION: RegExp = /^(\d+)\/(\d+)\s*/;

/** Decimal places kept when a fraction does not divide evenly (e.g. thirds). */
const FRACTION_PRECISION: number = 2;

/**
 * Render a whole part and a fraction as the decimal Figma shows, with no
 * trailing zeros: 1 + 1/2 → "1.5", 0 + 1/4 → "0.25", 2 + 1/3 → "2.33".
 */
function toDecimal(
  whole: number,
  numerator: number,
  denominator: number
): string {
  if (denominator === 0) {
    return String(whole);
  }
  const factor: number = 10 ** FRACTION_PRECISION;
  const value: number =
    Math.round((whole + numerator / denominator) * factor) / factor;
  return String(value);
}

/**
 * Split an option label into its large value and small unit.
 *
 * @param label - Raw option label from the engine, e.g. "10 minutes early".
 * @returns The two parts; `value` is `null` for non-numeric labels.
 *
 * @example
 * splitOptionLabel("10 minutes early");   // { value: "10", unit: "minutes early" }
 * splitOptionLabel("1.5 hours longer");   // { value: "1.5", unit: "hours longer" }
 * splitOptionLabel("1 1/2 hours longer"); // { value: "1.5", unit: "hours longer" }
 * splitOptionLabel("1/2 hour longer");    // { value: "0.5", unit: "hour longer" }
 * splitOptionLabel("On time");            // { value: null, unit: "On time" }
 */
export function splitOptionLabel(label: string): IOptionLabelParts {
  const mixed: RegExpMatchArray | null = label.match(LEADING_MIXED_FRACTION);
  if (mixed !== null) {
    return {
      value: toDecimal(Number(mixed[1]), Number(mixed[2]), Number(mixed[3])),
      unit: label.slice(mixed[0].length)
    };
  }

  const fraction: RegExpMatchArray | null = label.match(LEADING_FRACTION);
  if (fraction !== null) {
    return {
      value: toDecimal(0, Number(fraction[1]), Number(fraction[2])),
      unit: label.slice(fraction[0].length)
    };
  }

  const match: RegExpMatchArray | null = label.match(LEADING_NUMBER);

  if (match === null || match[1] === undefined) {
    return { value: null, unit: label };
  }

  return { value: match[1], unit: label.slice(match[0].length) };
}
